import { useState, useEffect, useCallback } from 'react';

type Platform = 'android' | 'ios' | 'unsupported';

const DISMISSED_KEY = 'pwa_install_dismissed_at';
const COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function detectPlatform(): Platform {
  const ua = navigator.userAgent;

  // iOS detection (includes iPad with desktop UA)
  if (/iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    // Only Safari can "Add to Home Screen" — exclude other iOS browsers
    if (/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua)) return 'unsupported';
    return 'ios';
  }

  if (/Android/.test(ua)) return 'android';

  return 'unsupported';
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    navigator.standalone === true ||
    document.referrer.includes('android-app://')
  );
}

function isDismissed(): boolean {
  const dismissedAt = localStorage.getItem(DISMISSED_KEY);
  if (!dismissedAt) return false;
  return Date.now() - Number(dismissedAt) < COOLDOWN_MS;
}

export function usePwaInstall() {
  const platform = detectPlatform();
  const [canShow, setCanShow] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    if (platform === 'unsupported' || isStandalone() || isDismissed()) {
      setCanShow(false);
      return;
    }

    // Android needs the beforeinstallprompt event to be useful
    if (platform === 'android' && !window.deferredPrompt) {
      // Listen for it in case it fires after mount
      const handler = () => setCanShow(true);
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }

    setCanShow(true);
  }, [platform]);

  // Hide permanently on install
  useEffect(() => {
    const handler = () => setCanShow(false);
    window.addEventListener('appinstalled', handler);
    return () => window.removeEventListener('appinstalled', handler);
  }, []);

  const triggerInstall = useCallback(async () => {
    const prompt = window.deferredPrompt;
    if (!prompt) return;

    setIsInstalling(true);
    try {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') {
        window.deferredPrompt = null;
      }
    } finally {
      setIsInstalling(false);
    }
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setCanShow(false);
  }, []);

  return { canShow, platform, triggerInstall, dismiss, isInstalling };
}
